import { EXO_SKELETON_EVENT_NAMES } from './consts';
import {
  createPostMessageEventHandler,
  ExoSkeletonRuntimeDataEvent,
  hasExoSkeletonListenerInitialized,
  sendRuntimeDataEvent,
} from './runtime';

export const MONITORING_HOST_REPORT_EVENT_NAME =
  EXO_SKELETON_EVENT_NAMES.MONITORING_HOST_REPORT_EVENT_NAME;

export type TReportType = 'info' | 'warning' | 'error';

export const REPORT_TYPES = {
  info: 'info',
  warning: 'warning',
  error: 'error',
} as { [key in TReportType]: TReportType };

export interface ReportData {
  scope?: string;
  // won't be used with Dynatrace
  type?: TReportType;
  // won't be used with Dynatrace
  value?: string | Error;
  context: Record<string, unknown>;
  UNSAFE__globalContext?: Record<string, unknown>;
}

type RuntimeMonitoringReportEvent = ExoSkeletonRuntimeDataEvent<
  typeof MONITORING_HOST_REPORT_EVENT_NAME,
  ReportData
>;

export interface CreateMonitoringListenerParameters {
  onReport(params: RuntimeMonitoringReportEvent): void;
}

export function isMonitoringReportDataEvent(
  data: unknown,
): data is RuntimeMonitoringReportEvent {
  return (
    (data as RuntimeMonitoringReportEvent)?.type ===
    MONITORING_HOST_REPORT_EVENT_NAME
  );
}

/**
 * Used to create a listener for the ExoSkeleton monitoring.
 */
export function createMonitoringListener({
  onReport,
}: CreateMonitoringListenerParameters) {
  return createPostMessageEventHandler(
    MONITORING_HOST_REPORT_EVENT_NAME,
    function ({ data }: MessageEvent<unknown>) {
      if (isMonitoringReportDataEvent(data)) {
        onReport(data);
      }
    },
  );
}

export function report(data: ReportData) {
  if (!hasExoSkeletonListenerInitialized(MONITORING_HOST_REPORT_EVENT_NAME)) {
    console.warn(
      '"report" was called, but the ExoSkeleton host did not initialize the "createMonitoringListener". Data sent:',
      data,
    );
  }

  if (!data.scope) {
    console.warn(
      '"report" should be called with a scope, please add your ExoSkeleton name, e.g. "accDemoApp"',
    );
  } else {
    data.context = {
      scope: data.scope,
      [data.scope]: data.context,
    };
  }

  if (data.UNSAFE__globalContext) {
    data.context = {
      ...data.context,
      ...data.UNSAFE__globalContext,
    };

    delete data.UNSAFE__globalContext;
  }

  return sendRuntimeDataEvent(MONITORING_HOST_REPORT_EVENT_NAME, data);
}
